import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import {
  TextField, Button, Typography, Link as MuiLink, Checkbox, 
  FormControlLabel, Grid, Paper, Avatar, 
  InputAdornment, IconButton, CircularProgress, Snackbar,
  Box, Container, Divider, Alert, Fade, useTheme, useMediaQuery
} from "@mui/material";

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import UpdateIcon from '@mui/icons-material/Update';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);

  const { login } = useAuth();
  const { tenant, refreshTenant } = useTenant();
  const { primaryColor } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const images = [
    '/images/enterprise-bg-1.jpg',
    '/images/enterprise-bg-2.jpg',
    '/images/enterprise-bg-3.jpg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [images.length]);

  const from = location.state?.from?.pathname || 
    (tenant ? '/member/dashboard' : '/');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const userData = await login(email, password);
      
      if (userData.passwordResetRequired === true) {
        sessionStorage.setItem('forcePasswordReset', 'true');
        navigate('/change-password', { 
          state: { 
            required: true,
            from: location.state?.from || '/dashboard'
          },
          replace: true
        });
        return;
      }

      if (userData.role === 'admin' && userData.status === 'pending') {
        setSnackbarOpen(true);
        setSnackbarMessage('Your admin account is pending approval. You will be notified once approved.');
        setTimeout(() => {
          navigate('/pending-approval');
        }, 3000);
        return;
      }

      if (userData.tenant) {
        localStorage.setItem('currentTenant', JSON.stringify(userData.tenant));
        if (userData.tenant._id || userData.tenant.id) {
          localStorage.setItem('tenantId', userData.tenant._id || userData.tenant.id);
        }
        await refreshTenant();
      } else if (userData.tenantId) {
        localStorage.setItem('tenantId', userData.tenantId);
        await refreshTenant();
      }

      setSnackbarOpen(true);
      setSnackbarMessage('Login successful');

      const userRole = userData?.role || 'member';
      let redirectTo = '/member/dashboard';
      
      if (userRole === 'superadmin') {
        redirectTo = '/superadmin/dashboard';
        localStorage.setItem('isAdmin', 'true');
      } else if (userRole === 'admin') {
        redirectTo = '/admin/dashboard'; 
        localStorage.setItem('isAdmin', 'true');
      } else if (userRole === 'staff') {
        redirectTo = '/staff/dashboard';
        localStorage.setItem('isStaff', 'true');
      }
      
      setTimeout(() => {
        navigate(redirectTo);
      }, 1000);
    } catch (error) {
      let errorMessage = error.message || 'An unknown error occurred';
      
      if (
        errorMessage.includes('starting up') || 
        errorMessage.includes('initializing') || 
        errorMessage.includes('Startup in progress')
      ) {
        errorMessage = 'The system is starting up. Please wait a moment and try again.';
        setTimeout(() => {
          setSnackbarOpen(true);
          setSnackbarMessage('Attempting to log in again...');
          setError('');
          handleLogin(new Event('auto-retry'));
        }, 5000);
      } else if (error.response) {
        const serverMessage = error.response.data?.message || error.response.data?.error;
        errorMessage = serverMessage || `Error: ${error.response.status} ${error.response.statusText}`;
        
        if (error.response.status === 401) {
          errorMessage = "Invalid email or password. Please try again.";
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your internet connection.';
      }
      
      setError(errorMessage);
      setSnackbarOpen(true);
      setSnackbarMessage(`Login failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const enterpriseFeatures = [
    {
      icon: <SecurityOutlinedIcon sx={{ fontSize: 20 }} />,
      text: "Enterprise-grade security"
    },
    {
      icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 20 }} />,
      text: "Advanced business tools"
    }
  ];

  const handleCheckStatus = () => {
    navigate('/pending-approval');
  };

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      width: "100%",
      overflow: "hidden",
      backgroundColor: "#f5f7fa"
    }}>
      <Box sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        backgroundImage: `
          linear-gradient(135deg, 
            ${primaryColor ? `${adjustColor(primaryColor, -60)}dd` : 'rgba(22, 41, 80, 0.92)'}, 
            ${primaryColor ? `${adjustColor(primaryColor, -40)}dd` : 'rgba(36, 59, 85, 0.95)'}
          ),
          url(${images[currentImageIndex]})
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 2s ease-in-out',
        zIndex: -1,
      }} />

      <Box sx={{ 
        position: "absolute", 
        top: 0, 
        left: 0, 
        width: "100%", 
        py: 3, 
        px: 4, 
        textAlign: "center" 
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          color="white" 
          fontWeight="700"
          sx={{ 
            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            letterSpacing: '0.5px'
          }}
        >
          {tenant?.name || "Enterprise Portal"}
        </Typography>
      </Box>

      <Container 
        maxWidth="sm" 
        sx={{ 
          position: "relative",
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          my: 4,
          px: { xs: 2, sm: 3 }
        }}
      >
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={24} 
            sx={{
              width: "100%",
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box 
              sx={{ 
                height: 8, 
                width: '100%',
                background: primaryColor 
                  ? `linear-gradient(90deg, ${adjustColor(primaryColor, -20)} 0%, ${primaryColor} 50%, ${adjustColor(primaryColor, 20)} 100%)` 
                  : 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)'
              }} 
            />
            
            <Box 
              sx={{ 
                pt: 6, 
                pb: 4, 
                px: { xs: 3, sm: 6 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center' 
              }}
            >
              <Box 
                sx={{ 
                  borderRadius: '50%',
                  background: primaryColor ? `${primaryColor}14` : 'rgba(25, 118, 210, 0.08)',
                  p: 1.5,
                  mb: 2
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: primaryColor || 'primary.main',
                    width: 56,
                    height: 56,
                    boxShadow: primaryColor 
                      ? `0 4px 12px ${primaryColor}40`
                      : '0 4px 12px rgba(25, 118, 210, 0.25)'
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight="700" 
                sx={{ mb: 1, color: 'text.primary' }}
              >
                {tenant?.name ? `${tenant?.name}` : "Sign In"}
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                align="center" 
                sx={{ mb: 4 }}
              >
                {tenant?.name 
                  ? "Enter your credentials to access your account" 
                  : "Welcome back! Please enter your credentials to continue."}
              </Typography>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    width: '100%', 
                    borderRadius: 2,
                    '& .MuiAlert-message': { fontWeight: 500 }
                  }}
                >
                  {error}
                </Alert>
              )}
              
              <Box 
                component="form" 
                onSubmit={handleLogin} 
                sx={{ 
                  width: '100%',
                  '& .MuiTextField-root': {
                    mb: 2.5
                  }
                }}
              >
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused': {
                        boxShadow: primaryColor 
                          ? `0 0 0 2px ${primaryColor}30` 
                          : '0 0 0 2px rgba(25, 118, 210, 0.15)'
                      }
                    }
                  }}
                />
                
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused': {
                        boxShadow: primaryColor 
                          ? `0 0 0 2px ${primaryColor}30` 
                          : '0 0 0 2px rgba(25, 118, 210, 0.15)'
                      }
                    }
                  }}
                />
                
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          color="primary" 
                          size="small"
                          sx={{
                            color: primaryColor ? primaryColor : undefined,
                            '&.Mui-checked': {
                              color: primaryColor ? primaryColor : undefined,
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">Remember me</Typography>
                      }
                    />
                  </Grid>
                  <Grid item>
                    <MuiLink 
                      component={Link} 
                      to="/reset-password" 
                      variant="body2"
                      sx={{ 
                        color: primaryColor || 'primary.main',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Forgot password?
                    </MuiLink>
                  </Grid>
                </Grid>
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    mb: 3,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    position: 'relative',
                    overflow: 'hidden',
                    background: primaryColor 
                      ? `linear-gradient(90deg, ${adjustColor(primaryColor, -20)} 0%, ${primaryColor} 50%, ${adjustColor(primaryColor, 20)} 100%)`
                      : 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)',
                    '&:hover': {
                      background: primaryColor 
                        ? `linear-gradient(90deg, ${adjustColor(primaryColor, -30)} 0%, ${adjustColor(primaryColor, -10)} 50%, ${adjustColor(primaryColor, 10)} 100%)`
                        : 'linear-gradient(90deg, #004c9e 0%, #005bb8 50%, #1976d2 100%)',
                      boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    "Sign In"
                  )}
                </Button>
                
                {/* Commenting out social login options and divider 
                <Divider sx={{ my: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary', 
                      px: 1,
                      fontSize: '0.875rem' 
                    }}
                  >
                    Or continue with
                  </Typography>
                </Divider>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GoogleIcon />}
                      sx={{ 
                        py: 1.2,
                        borderRadius: 2,
                        color: '#5f6368',
                        borderColor: '#dadce0',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: primaryColor ? `${primaryColor}40` : '#d2e3fc',
                          background: primaryColor ? `${primaryColor}08` : 'rgba(66, 133, 244, 0.04)'
                        }
                      }}
                    >
                      Google
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<LinkedInIcon />}
                      sx={{ 
                        py: 1.2,
                        borderRadius: 2,
                        color: primaryColor || '#0a66c2',
                        borderColor: '#dadce0',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: primaryColor ? `${primaryColor}40` : '#a6c4e0',
                          background: primaryColor ? `${primaryColor}08` : 'rgba(10, 102, 194, 0.04)'
                        }
                      }}
                    >
                      LinkedIn
                    </Button>
                  </Grid>
                </Grid>
                */}
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    Don't have an account?{' '}
                    <MuiLink 
                      component={Link}
                      to="/register"
                      sx={{ 
                        color: primaryColor || 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Sign up
                    </MuiLink>
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Need to create a new organization?{' '}
                    <MuiLink 
                      component={Link}
                      to="/register-tenant"
                      sx={{ 
                        color: primaryColor || 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Register your organization
                    </MuiLink>
                  </Typography>
                  
                  <Button
                    variant="text"
                    sx={{ 
                      mt: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2,
                      px: 2,
                      color: primaryColor || 'secondary.main'
                    }}
                    startIcon={<UpdateIcon />}
                    onClick={handleCheckStatus}
                    size="small"
                  >
                    Check Registration Status
                  </Button>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                py: 2, 
                px: 3,
                bgcolor: primaryColor ? `${primaryColor}08` : 'rgba(25, 118, 210, 0.04)',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Grid container spacing={3} justifyContent="center">
                {enterpriseFeatures.map((feature, index) => (
                  <Grid item key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ color: primaryColor || 'primary.main', mr: 1, display: 'flex' }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {feature.text}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Fade>
        
        <Typography 
          variant="caption" 
          color="white" 
          align="center" 
          sx={{ 
            mt: 4, 
            opacity: 0.85, 
            textShadow: '0 1px 2px rgba(0,0,0,0.3)' 
          }}
        >
          © {new Date().getFullYear()} {tenant?.name || "Enterprise Portal"}. All rights reserved.
        </Typography>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={error ? "error" : "success"}
          variant="filled"
          sx={{ 
            width: '100%', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontWeight: 500,
            bgcolor: error ? 'error.main' : (primaryColor || 'success.main')
          }}
        >
          {snackbarMessage || (error ? error : 'Login successful')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function adjustColor(color, amount) {
  if (!color) return amount > 0 ? '#6366f1' : '#3b82f6';
  
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    const componentToHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  };

  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  return rgbToHex(
    Math.min(255, Math.max(0, rgb.r + amount)),
    Math.min(255, Math.max(0, rgb.g + amount)),
    Math.min(255, Math.max(0, rgb.b + amount))
  );
}

export default LoginPage;
